<!-- ============================================================================
     AdminVouchers.vue - Gestion des Crédits Carburant (Version corrigée)
     ============================================================================ -->
<template>
  <avs-card>
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template #title>
      <v-toolbar flat class="mb-4 bgTable">
        <!-- Recherche globale -->
        <v-text-field
          v-model="search"
          dense
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('Recherche (matricule, chauffeur, téléphone...)')"
          class="mr-4"
          style="max-width: 320px;"
          @input="debouncedSearch"
        />

        <!-- Filtre par type -->
        <v-select
          v-model="filterType"
          :items="typeOptions"
          item-text="text"
          item-value="value"
          dense
          hide-details
          :label="$t('Type')"
          class="mr-4"
          style="max-width: 160px;"
          @change="onFiltersChange"
        />

        <!-- Filtre par statut activation -->
        <v-select
          v-model="filterActivation"
          :items="activationOptions"
          item-text="text"
          item-value="value"
          dense
          hide-details
          :label="$t('État')"
          class="mr-4"
          style="max-width: 140px;"
          @change="onFiltersChange"
        />

        <!-- Filtre par utilisation -->
        <v-select
          v-model="filterUsed"
          :items="usedOptions"
          item-text="text"
          item-value="value"
          dense
          hide-details
          :label="$t('Utilisation')"
          class="mr-4"
          style="max-width: 140px;"
          @change="onFiltersChange"
        />

        <v-spacer />

        <!-- Bouton creer un bon -->
        <v-btn color="primary" class="mr-2" depressed @click="openCreate">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('Nouveau bon') }}
        </v-btn>

        <!-- Bouton actions en masse -->
        <v-btn
          v-if="selectedItems.length > 0"
          color="warning"
          class="mr-2"
          @click="openBulkActionDialog"
        >
          <v-icon left>mdi-cog</v-icon>
          {{ $t('Actions') }} ({{ selectedItems.length }})
        </v-btn>

        <!-- Bouton refresh -->
        <v-btn icon @click="fetchReset()" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Stats rapides -->
      <v-row dense class="mx-4 mb-4">
        <v-col cols="6" sm="3" md="2">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 primary--text">{{ stats.total_active || 0 }}</div>
            <div class="caption">{{ $t('Actifs') }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 success--text">{{ stats.total_vouchers || 0 }}</div>
            <div class="caption">{{ $t('Total') }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 grey--text">{{ stats.total_used || 0 }}</div>
            <div class="caption">{{ $t('Utilisés') }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 error--text">{{ stats.total_expired || 0 }}</div>
            <div class="caption">{{ $t('Expirés') }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 info--text">{{ formatNumber(stats.total_liters_remaining) }} L</div>
            <div class="caption">{{ $t('Litres restants') }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 info--text">{{ formatAmount(stats.total_amount_remaining) }}</div>
            <div class="caption">{{ $t('XOF restants') }}</div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- ===== CONTENU ===== -->
    <template #content>
      <v-data-table
        v-model="selectedItems"
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        disable-pagination
        hide-default-footer
        show-select
        dense
        class="elevation-0 ma-6 bgTable"
      >
        <!-- Type de voucher -->
        <template #item.voucher_type="{ item }">
          <v-chip
            x-small
            :color="getTypeColor(item.voucher_type)"
            text-color="white"
          >
            <v-icon x-small left>{{ getTypeIcon(item.voucher_type) }}</v-icon>
            {{ getTypeLabel(item.voucher_type) }}
          </v-chip>
        </template>

        <!-- Bénéficiaire -->
        <template #item.beneficiary="{ item }">
          <div v-if="item.vehicle">
            <div class="font-weight-medium">
              <v-icon small class="mr-1">mdi-car</v-icon>
              {{ item.vehicle_registration }}
            </div>
            <div class="caption grey--text" v-if="item.vehicle_driver_name">
              {{ item.vehicle_driver_name }}
            </div>
            <div class="caption grey--text">
              {{ item.vehicle_company_name }}
            </div>
          </div>
          <div v-else-if="item.loyalty_user">
            <div class="font-weight-medium">
              <v-icon small class="mr-1">mdi-account</v-icon>
              {{ item.loyalty_user_name }}
            </div>
            <div class="caption grey--text">
              {{ item.loyalty_user_phone || 'N/A' }}
            </div>
          </div>
          <span v-else class="grey--text">-</span>
        </template>

        <!-- Valeur initiale -->
        <template #item.initial_value="{ item }">
          <div v-if="item.is_all_money">
            <span class="font-weight-medium">{{ formatAmount(item.purchase_amount) }} XOF</span>
          </div>
          <div v-else>
            <span class="font-weight-medium">{{ formatNumber(item.purchase_liters) }} L</span>
            <div class="caption grey--text">{{ formatAmount(item.purchase_amount) }} XOF</div>
          </div>
        </template>

        <!-- Solde restant -->
        <template #item.remaining="{ item }">
          <div v-if="item.is_all_used">
            <v-chip x-small color="grey" text-color="white">Épuisé</v-chip>
          </div>
          <div v-else>
            <div v-if="item.is_all_money">
              <span 
                class="font-weight-medium"
                :class="getRemainClass(item.percent_remaining)"
              >
                {{ formatAmount(item.remaining_amount) }} XOF
              </span>
            </div>
            <div v-else>
              <span 
                class="font-weight-medium"
                :class="getRemainClass(item.percent_remaining)"
              >
                {{ formatNumber(item.liter_remaining) }} L
              </span>
              <div class="caption grey--text">{{ formatAmount(item.remaining_amount) }} XOF</div>
            </div>
            <!-- Progress bar -->
            <v-progress-linear
              :value="item.percent_remaining"
              :color="getProgressColor(item.percent_remaining)"
              height="4"
              class="mt-1"
              rounded
            />
          </div>
        </template>

        <!-- Produit -->
        <template #item.product_name="{ item }">
          {{ item.product_name || '-' }}
        </template>

        <!-- Statut -->
        <template #item.status="{ item }">
          <v-chip
            x-small
            :color="getStatusColor(item.status)"
            text-color="white"
          >
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- État activation -->
        <template #item.is_activated="{ item }">
          <v-chip
            x-small
            :color="item.is_activated ? 'success' : 'grey'"
            text-color="white"
          >
            {{ item.is_activated ? $t('Actif') : $t('Inactif') }}
          </v-chip>
        </template>

        <!-- Date expiration -->
        <template #item.valid_until="{ item }">
          <div v-if="item.valid_until">
            <span :class="{ 'error--text': item.is_expired }">
              {{ formatDatetime(item.valid_until) }}
            </span>
            <v-icon v-if="item.is_expired" small color="error" class="ml-1">mdi-alert</v-icon>
          </div>
          <span v-else class="grey--text">{{ $t('Illimité') }}</span>
        </template>

        <!-- Date création -->
        <template #item.created_at="{ item }">
          {{ formatDatetime(item.created_at) }}
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex align-center justify-end">
            <!-- Toggle activation -->
            <avs-button
              icon
              :color="item.is_activated ? 'error' : 'success'"
              :iconI="item.is_activated ? 'mdi-pause-circle' : 'mdi-play-circle'"
              label
              fab
              small
              :spantext="item.is_activated ? $t('Désactiver') : $t('Activer')"
              @click.stop="toggleActivation(item)"
            />

            <!-- Modifier -->
            <avs-button
              icon
              color="primary"
              iconI="mdi-pencil"
              label
              fab
              small
              :loading="editLoading && editItem && editItem.id === item.id"
              :spantext="$t('Modifier')"
              @click.stop="openEditDialog(item)"
            />

            <!-- Voir détails -->
            <avs-button
              icon
              color="info"
              iconI="mdi-eye"
              label
              fab
              small
              :spantext="$t('Voir détails')"
              @click.stop="openDetailDialog(item)"
            />
          </div>
        </template>
      </v-data-table>

      <!-- Loader scroll infini -->
      <div class="py-4 d-flex justify-center" v-if="loadingMore">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length"
        class="text-center grey--text py-4"
      >
        {{ $t('Fin de la liste') }}
      </div>

      <!-- Sentinelle pour IntersectionObserver -->
      <div ref="sentinel" style="height:1px;"></div>

      <!-- ===== DIALOG MODIFICATION ===== -->
      <v-dialog v-model="editDialog" max-width="700" persistent>
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-pencil</v-icon>
            {{ $t('Modifier le crédit carburant') }}
            <v-spacer />
            <v-btn icon dark @click="editDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4" v-if="editItem">
            <v-form ref="editForm">
              <!-- Info bénéficiaire (lecture seule) -->
              <v-alert type="info" dense text class="mb-4">
                <strong>{{ $t('Bénéficiaire') }}:</strong>
                <span v-if="editItem.vehicle">
                  {{ editItem.vehicle_registration }} 
                  <span v-if="editItem.vehicle_driver_name">- {{ editItem.vehicle_driver_name }}</span>
                </span>
                <span v-else-if="editItem.loyalty_user_name">
                  {{ editItem.loyalty_user_name }} ({{ editItem.loyalty_user_phone }})
                </span>
              </v-alert>

              <v-row dense>
                <!-- Litres initiaux -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editForm.purchase_liters"
                    :label="$t('Litres initiaux')"
                    type="number"
                    dense
                    outlined
                    suffix="L"
                    :disabled="editItem.is_all_money"
                  />
                </v-col>

                <!-- Litres restants -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editForm.liter_remaining"
                    :label="$t('Litres restants')"
                    type="number"
                    dense
                    outlined
                    suffix="L"
                    :disabled="editItem.is_all_money"
                  />
                </v-col>

                <!-- Montant initial -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editForm.purchase_amount"
                    :label="$t('Montant initial')"
                    type="number"
                    dense
                    outlined
                    suffix="XOF"
                  />
                </v-col>

                <!-- Montant restant -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editForm.remaining_amount"
                    :label="$t('Montant restant')"
                    type="number"
                    dense
                    outlined
                    suffix="XOF"
                  />
                </v-col>

                <!-- Date d'expiration -->
                <v-col cols="12" md="6">
                  <DatetimePicker
                    v-model="editForm.valid_until"
                    :label="$t('Expiration')"
                    :textFieldProps="{ placeholder: $t('Date d expiration') }"
                  />
                </v-col>

                <!-- Statut -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editForm.status"
                    :items="statusOptionsEdit"
                    item-text="text"
                    item-value="value"
                    :label="$t('Statut')"
                    dense
                    outlined
                  />
                </v-col>

                <!-- Options -->
                <v-col cols="12">
                  <v-row dense>
                    <v-col cols="6" md="4">
                      <v-checkbox
                        v-model="editForm.is_activated"
                        :label="$t('Activé')"
                        hide-details
                        class="mt-0"
                      />
                    </v-col>
                    <v-col cols="6" md="4">
                      <v-checkbox
                        v-model="editForm.is_all_used"
                        :label="$t('Épuisé')"
                        hide-details
                        class="mt-0"
                      />
                    </v-col>
                    <v-col cols="6" md="4">
                      <v-checkbox
                        v-model="editForm.allow_successive"
                        :label="$t('Allow successive')"
                        hide-details
                        class="mt-0"
                      />
                    </v-col>
                  </v-row>
                </v-col>

                <!-- BL Number -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.bl_number"
                    :label="$t('Numéro BL')"
                    dense
                    outlined
                  />
                </v-col>

                <!-- Destination -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.destination"
                    :label="$t('Destination')"
                    dense
                    outlined
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-btn text @click="editDialog = false">
              {{ $t('Annuler') }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :loading="editSubmitting"
              @click="submitEdit"
            >
              <v-icon left>mdi-check</v-icon>
              {{ $t('Enregistrer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== DIALOG DÉTAILS ===== -->
      <v-dialog v-model="detailDialog" max-width="800">
        <v-card v-if="detailItem">
          <v-card-title>
            <v-icon left>mdi-gas-station</v-icon>
            {{ $t('Détails du crédit carburant') }}
            <v-spacer />
            <v-btn icon @click="detailDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row dense>
              <!-- Infos bénéficiaire -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3">
                  <div class="subtitle-2 mb-2">{{ $t('Bénéficiaire') }}</div>
                  <div v-if="detailItem.vehicle_info">
                    <div><strong>Véhicule:</strong> {{ detailItem.vehicle_info.registration_number }}</div>
                    <div v-if="detailItem.vehicle_info.driver_name">
                      <strong>Chauffeur:</strong> {{ detailItem.vehicle_info.driver_name }}
                    </div>
                    <div v-if="detailItem.vehicle_info.driver_phone">
                      <strong>Tél:</strong> {{ detailItem.vehicle_info.driver_phone }}
                    </div>
                    <div><strong>Entreprise:</strong> {{ detailItem.vehicle_info.company_name }}</div>
                  </div>
                  <div v-else-if="detailItem.loyalty_user_info">
                    <div><strong>Utilisateur:</strong> {{ detailItem.loyalty_user_info.fullname }}</div>
                    <div><strong>Tél:</strong> {{ detailItem.loyalty_user_info.phone_number }}</div>
                    <div v-if="detailItem.loyalty_user_info.email">
                      <strong>Email:</strong> {{ detailItem.loyalty_user_info.email }}
                    </div>
                  </div>
                  <div v-else class="grey--text">Non défini</div>
                </v-card>
              </v-col>

              <!-- Valeurs -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3">
                  <div class="subtitle-2 mb-2">{{ $t('Valeurs') }}</div>
                  <div><strong>Litres initiaux:</strong> {{ detailItem.purchase_liters }} L</div>
                  <div><strong>Litres restants:</strong> {{ detailItem.liter_remaining }} L</div>
                  <div><strong>Montant initial:</strong> {{ formatAmount(detailItem.purchase_amount) }} XOF</div>
                  <div><strong>Montant restant:</strong> {{ formatAmount(detailItem.remaining_amount) }} XOF</div>
                  <v-progress-linear
                    :value="detailItem.percent_remaining || 0"
                    :color="getProgressColor(detailItem.percent_remaining || 0)"
                    height="8"
                    class="mt-2"
                    rounded
                  />
                  <div class="caption text-center">{{ detailItem.percent_remaining || 0 }}% restant</div>
                </v-card>
              </v-col>

              <!-- Statuts -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3">
                  <div class="subtitle-2 mb-2">{{ $t('Statuts') }}</div>
                  <div>
                    <strong>Statut:</strong>
                    <v-chip x-small :color="getStatusColor(detailItem.status)" text-color="white" class="ml-2">
                      {{ getStatusLabel(detailItem.status) }}
                    </v-chip>
                  </div>
                  <div>
                    <strong>Activé:</strong>
                    <v-icon small :color="detailItem.is_activated ? 'success' : 'grey'" class="ml-2">
                      {{ detailItem.is_activated ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </div>
                  <div>
                    <strong>Épuisé:</strong>
                    <v-icon small :color="detailItem.is_all_used ? 'error' : 'grey'" class="ml-2">
                      {{ detailItem.is_all_used ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </div>
                  <div>
                    <strong>Issu de points:</strong>
                    <v-icon small :color="detailItem.is_from_points ? 'info' : 'grey'" class="ml-2">
                      {{ detailItem.is_from_points ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </div>
                </v-card>
              </v-col>

              <!-- Dates -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3">
                  <div class="subtitle-2 mb-2">{{ $t('Dates') }}</div>
                  <div><strong>Créé le:</strong> {{ formatDatetime(detailItem.created_at) }}</div>
                  <div><strong>Modifié le:</strong> {{ formatDatetime(detailItem.updated_at) }}</div>
                  <div>
                    <strong>Expire le:</strong> 
                    <span :class="{ 'error--text': detailItem.is_expired }">
                      {{ detailItem.valid_until ? formatDatetime(detailItem.valid_until) : 'Illimité' }}
                    </span>
                    <v-icon v-if="detailItem.is_expired" small color="error" class="ml-1">mdi-alert</v-icon>
                  </div>
                  <div v-if="detailItem.approved_at">
                    <strong>Approuvé le:</strong> {{ formatDatetime(detailItem.approved_at) }}
                  </div>
                </v-card>
              </v-col>

              <!-- Autres infos -->
              <v-col cols="12">
                <v-card outlined class="pa-3">
                  <div class="subtitle-2 mb-2">{{ $t('Autres informations') }}</div>
                  <v-row dense>
                    <v-col cols="6" md="3">
                      <strong>Produit:</strong><br>
                      {{ getNestedValue(detailItem, 'product_info.name') || '-' }}
                    </v-col>
                    <v-col cols="6" md="3">
                      <strong>Station:</strong><br>
                      {{ getNestedValue(detailItem, 'shop_info.name') || '-' }}
                    </v-col>
                    <v-col cols="6" md="3">
                      <strong>BL:</strong><br>
                      {{ detailItem.bl_number || '-' }}
                    </v-col>
                    <v-col cols="6" md="3">
                      <strong>Destination:</strong><br>
                      {{ detailItem.destination || '-' }}
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="detailDialog = false">
              {{ $t('Fermer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== DIALOG ACTIONS EN MASSE ===== -->
      <v-dialog v-model="bulkDialog" max-width="500">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-cog</v-icon>
            {{ $t('Actions en masse') }}
          </v-card-title>

          <v-card-text>
            <p>{{ selectedItems.length }} crédit(s) carburant sélectionné(s).</p>
            <v-radio-group v-model="bulkAction">
              <v-radio
                value="activate"
                :label="$t('Activer')"
                color="success"
              />
              <v-radio
                value="deactivate"
                :label="$t('Désactiver')"
                color="warning"
              />
              <v-radio
                value="mark_used"
                :label="$t('Marquer comme épuisé')"
                color="error"
              />
            </v-radio-group>
          </v-card-text>

          <v-card-actions>
            <v-btn text @click="bulkDialog = false">
              {{ $t('Annuler') }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :loading="bulkSubmitting"
              :disabled="!bulkAction"
              @click="submitBulkAction"
            >
              {{ $t('Appliquer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== Dialog : Creation d'un bon (admin) ===== -->
      <v-dialog v-model="createDialog" max-width="900" persistent>
        <AdminVoucherEditor
          v-if="createDialog"
          @close="createDialog = false"
          @saved="onVoucherCreated"
        />
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import debounce from "lodash/debounce"
import AdminVoucherEditor from "./AdminVoucherEditor.vue"
import { stringToDate } from "@/utils/datetime"

export default {
  name: "AdminVouchers",
  components: {
    AdminVoucherEditor,
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
  },
  
  data() {
    return {
      // État
      loading: false,
      loadingMore: false,
      items: [],
      totalItems: 0,
      selectedItems: [],
      stats: {},
      page: 1,
      hasMore: true,
      observer: null,

      // Dialog creation
      createDialog: false,

      // Filtres
      search: "",
      filterType: "all",
      filterActivation: "true",
      filterUsed: "all",
      
      // Options des filtres
      typeOptions: [
        { text: "Tous", value: "all" },
        { text: "Véhicules", value: "vehicle" },
        { text: "Fidélité", value: "loyalty" },
        { text: "Points convertis", value: "points" },
      ],
      activationOptions: [
        { text: "Tous", value: "all" },
        { text: "Actifs", value: "true" },
        { text: "Inactifs", value: "false" },
      ],
      usedOptions: [
        { text: "Tous", value: "all" },
        { text: "En cours", value: "false" },
        { text: "Épuisés", value: "true" },
      ],
      statusOptionsEdit: [
        { text: "En attente", value: "pending" },
        { text: "Approuvé", value: "approved" },
        { text: "Rejeté", value: "rejected" },
      ],
      
      // Dialog modification
      editDialog: false,
      editItem: null,
      editForm: {
        purchase_liters: null,
        purchase_amount: null,
        liter_remaining: null,
        remaining_amount: null,
        valid_until: null,
        status: null,
        is_activated: false,
        is_all_used: false,
        allow_successive: false,
        bl_number: "",
        destination: "",
      },
      editSubmitting: false,
      editLoading: false,
      
      // Dialog détails
      detailDialog: false,
      detailItem: null,
      detailLoading: false,
      
      // Dialog bulk
      bulkDialog: false,
      bulkAction: null,
      bulkSubmitting: false,
      
      // Headers du tableau
      headers: [
        { text: "Type", value: "voucher_type", width: "100" },
        { text: "Bénéficiaire", value: "beneficiary", sortable: false, width: "200" },
        { text: "Valeur initiale", value: "initial_value", sortable: false, width: "130" },
        { text: "Solde restant", value: "remaining", sortable: false, width: "150" },
        { text: "Produit", value: "product_name", width: "100" },
        { text: "Statut", value: "status", width: "100" },
        { text: "État", value: "is_activated", width: "80" },
        { text: "Expiration", value: "valid_until", width: "160" },
        { text: "Créé le", value: "created_at", width: "140" },
        { text: "", value: "actions", sortable: false, align: "end", width: "140" },
      ],
    }
  },
  
  created() {
    this.debouncedSearch = debounce(this.onFiltersChange, 400)
  },

  mounted() {
    this.fetchStats()
    this.fetchReset()
    this.$nextTick(this.createObserver)
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  
  methods: {
    // ===== CREATION (admin) =====
    openCreate() {
      this.createDialog = true
    },
    onVoucherCreated() {
      this.createDialog = false
      this.fetchStats()
      this.fetchReset()
    },

    // ===== HELPER pour accéder aux propriétés imbriquées =====
    getNestedValue(obj, path) {
      if (!obj || !path) return null
      return path.split('.').reduce((acc, key) => acc && acc[key], obj)
    },
    
    // ===== FORMATAGE =====
    formatAmount(val) {
      if (val === null || val === undefined) return "0"
      return Number(val).toLocaleString("fr-FR")
    },
    
    formatNumber(val) {
      if (val === null || val === undefined) return "0"
      return Number(val).toLocaleString("fr-FR", { maximumFractionDigits: 2 })
    },
    
    formatDate(dateStr) {
      if (!dateStr) return "-"
      try {
        return new Date(dateStr).toLocaleDateString("fr-FR")
      } catch (e) {
        return "-"
      }
    },
    
    formatDatetime(dateStr) {
      if (!dateStr) return "-"
      try {
        return new Date(dateStr).toLocaleString("fr-FR")
      } catch (e) {
        return "-"
      }
    },
    
    // ===== COULEURS & LABELS =====
    getTypeColor(type) {
      const colors = {
        vehicle: "primary",
        loyalty: "purple",
        points: "orange",
      }
      return colors[type] || "grey"
    },
    
    getTypeIcon(type) {
      const icons = {
        vehicle: "mdi-car",
        loyalty: "mdi-account",
        points: "mdi-star",
      }
      return icons[type] || "mdi-help"
    },
    
    getTypeLabel(type) {
      const labels = {
        vehicle: "Véhicule",
        loyalty: "Fidélité",
        points: "Points",
      }
      return labels[type] || type
    },
    
    getStatusColor(status) {
      const colors = {
        pending: "warning",
        approved: "success",
        rejected: "error",
      }
      return colors[status] || "grey"
    },
    
    getStatusLabel(status) {
      const labels = {
        pending: "En attente",
        approved: "Approuvé",
        rejected: "Rejeté",
      }
      return labels[status] || status
    },
    
    getRemainClass(percent) {
      if (percent <= 20) return "error--text"
      if (percent <= 50) return "warning--text"
      return "success--text"
    },
    
    getProgressColor(percent) {
      if (percent <= 20) return "error"
      if (percent <= 50) return "warning"
      return "success"
    },
    
    // ===== FETCH DATA =====
    getParams() {
      const params = { page: this.page }
      if (this.search) params.search = this.search
      if (this.filterType !== "all") params.type = this.filterType
      if (this.filterActivation !== "all") params.is_activated = this.filterActivation
      if (this.filterUsed !== "all") params.is_all_used = this.filterUsed
      return params
    },

    async fetchData({ reset = false } = {}) {
      if (reset) this.loading = true
      else this.loadingMore = true

      try {
        const data = await AdminServices.getVouchers(this, this.getParams())
        let results = []

        if (data.results) {
          results = data.results
          this.totalItems = data.count || data.results.length
        } else if (Array.isArray(data)) {
          results = data
          this.totalItems = data.length
        }

        this.items = reset ? results : this.items.concat(results)
        this.hasMore = !!(data && data.next)
      } catch (e) {
        if (e?.response?.status === 404) this.hasMore = false
        console.error("Erreur fetch vouchers:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Erreur lors du chargement des crédits carburant."),
          color: "error",
        })
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => {
          if (this.hasMore && this.observer && this.$refs.sentinel) {
            this.observer.unobserve(this.$refs.sentinel)
            this.observer.observe(this.$refs.sentinel)
          }
        })
      }
    },

    fetchReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.selectedItems = []
      this.fetchData({ reset: true })
    },

    async fetchStats() {
      try {
        const data = await AdminServices.getVouchersStats(this)
        this.stats = data || {}
      } catch (e) {
        console.error("Erreur fetch stats:", e)
      }
    },

    // ===== INFINITE SCROLL =====
    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) this.loadMore() },
        { root: null, rootMargin: "200px", threshold: 0.1 }
      )
      this.observer.observe(el)
    },

    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetchData()
    },

    // ===== EVENTS =====
    onFiltersChange() {
      this.fetchReset()
    },
    
    // ===== TOGGLE ACTIVATION =====
    async toggleActivation(item) {
      try {
        await AdminServices.toggleVoucherActivation(this, item.id)
        
        // Mettre à jour localement
        item.is_activated = !item.is_activated
        
        this.$store.commit("app/message", {
          msg: item.is_activated 
            ? this.$t("Crédit carburant activé.") 
            : this.$t("Crédit carburant désactivé."),
          color: "success",
        })
        
        // Refresh stats
        this.fetchStats()
      } catch (e) {
        console.error("Erreur toggle:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Erreur lors de la modification."),
          color: "error",
        })
      }
    },
    
    // ===== EDIT DIALOG =====
    async openEditDialog(item) {
      this.editLoading = true
      try {
        // GET sur le detail pour avoir la donnee fraiche, comme dans le menu Credit carburant
        const detail = await AdminServices.getVoucherDetail(this, item.id)
        this.editItem = { ...detail }
        this.editForm = {
          purchase_liters: detail.purchase_liters,
          purchase_amount: detail.purchase_amount,
          liter_remaining: detail.liter_remaining,
          remaining_amount: detail.remaining_amount,
          // valid_until doit etre un moment object pour DatetimePicker
          valid_until: detail.valid_until ? stringToDate(detail.valid_until) : null,
          status: detail.status,
          is_activated: detail.is_activated || false,
          is_all_used: detail.is_all_used || false,
          allow_successive: detail.allow_successive || false,
          bl_number: detail.bl_number || "",
          destination: detail.destination || "",
        }
        this.editDialog = true
      } catch (e) {
        console.error("Erreur fetch detail:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Erreur lors du chargement des details."),
          color: "error",
        })
      } finally {
        this.editLoading = false
      }
    },


    async submitEdit() {
      this.editSubmitting = true
      // editForm.valid_until est un moment object; JSON.stringify appelle .toJSON()
      // qui produit un ISO 8601 UTC ("2026-05-08T23:59:00.000Z") accepte par DRF.
      try {
        await AdminServices.updateVoucher(this, this.editItem.id, this.editForm)
        
        this.$store.commit("app/message", {
          msg: this.$t("Crédit carburant modifié avec succès."),
          color: "success",
        })
        
        this.editDialog = false
        this.fetchReset()
        this.fetchStats()
      } catch (e) {
        console.error("Erreur update:", e)
        this.$store.commit("app/message", {
          msg: e?.response?.data?.detail || this.$t("Erreur lors de la modification."),
          color: "error",
        })
      } finally {
        this.editSubmitting = false
      }
    },
    
    // ===== DETAIL DIALOG =====
    async openDetailDialog(item) {
      this.detailLoading = true
      this.detailItem = null
      
      try {
        const data = await AdminServices.getVoucherDetail(this, item.id)
        console.log("Detail data received:", data) // Debug log
        this.detailItem = data
        // Ouvrir la modal APRÈS avoir reçu les données
        this.$nextTick(() => {
          this.detailDialog = true
        })
      } catch (e) {
        console.error("Erreur detail:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Erreur lors du chargement des détails."),
          color: "error",
        })
      } finally {
        this.detailLoading = false
      }
    },
    
    // ===== BULK ACTIONS =====
    openBulkActionDialog() {
      this.bulkAction = null
      this.bulkDialog = true
    },
    
    async submitBulkAction() {
      if (!this.bulkAction || this.selectedItems.length === 0) return
      
      this.bulkSubmitting = true
      
      try {
        const ids = this.selectedItems.map(item => item.id)
        await AdminServices.bulkVoucherAction(this, {
          ids,
          action: this.bulkAction,
        })
        
        this.$store.commit("app/message", {
          msg: this.$t("Action appliquée avec succès."),
          color: "success",
        })
        
        this.bulkDialog = false
        this.selectedItems = []
        this.fetchReset()
        this.fetchStats()
      } catch (e) {
        console.error("Erreur bulk:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Erreur lors de l'action en masse."),
          color: "error",
        })
      } finally {
        this.bulkSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.v-card__title.primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}
</style>